import { contextDialogEnabled, fitmentSearchEnabled, searchPageEnabled } from 'BuildArtifacts/featureFlags';
import { type ComponentChildren, render } from 'preact';
import { Provider } from 'react-redux';

import { initializeAddons } from 'Addons';
import getFitmentSearchAPI from 'Addons/fitmentSearch/fitmentSearchAPI';
import { ContextDialog, SearchPage } from 'Components';
import makeTemplateApplicator from 'Components/components';
import type { ContextDialogTemplateParams } from 'Components/dialog/contextDialog';
import ErrorBoundary from 'Components/errorBoundary';
import { ContextDialogName } from 'Components/names';
import type { SearchPageTemplateParams } from 'Components/searchPage';
import type { TemplateContextMap, TemplateFunction } from 'Components/types';
import { clearAutocompleteQuery, setAutocompleteQuery } from 'Core/actions/autocomplete';
import { dialogOpened, type OpenDialogMetadata } from 'Core/actions/dialog';
import {
  sendFitmentTableRequest,
  sendInitFitmentSearchRequest,
  sendVerifyFitmentRequest,
} from 'Core/actions/fitmentSearch/index';
import { sendInitHtmxRequest } from 'Core/actions/htmx';
import { setProductData } from 'Core/actions/product';
import { replaceInHistory } from 'Core/actions/redirect';
import {
  discardManyFields,
  discardQuery,
  filterQueryResetRequested,
  replaceRequest,
  resetRequest,
  setSort,
} from 'Core/actions/request';
import history, { type Location as HistoryLocation, uriChangeHandled } from 'Core/history';
import { initializeVehicleStorage } from 'Core/reducers/fitmentSearch/storage';
import { autocompleteRequestQuerySelector } from 'Core/selectors/autocomplete';
import { createSelectedVehicleSelectionSelector } from 'Core/selectors/fitmentSearch';
import { searchRequestFilterQuerySelector, searchRequestQuerySelector } from 'Core/selectors/search';
import store from 'Core/store';
import { logNetworkRequest, logRuntimeError } from 'Diagnostics/diagnostics';
import { isSearchItemRawData } from 'Models/searchItemData';
import { jsScriptDomain } from 'Models/serverConfiguration';
import { mainConfig } from 'Models/uiConfig/componentlessWidgetsConfig';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import productConfig from 'Models/uiConfig/productConfig';
import requestConfig from 'Models/uiConfig/requestConfig';
import type { RequestExtra } from 'Models/uiConfig/requestTypes';
import uiConfig from 'Models/uiConfig/uiConfig';
import widgetsConfig from 'Models/uiConfig/widgetsConfig';
import { loadAutoSyncVisualizerScript } from 'Modules/autoSync/index';
import type { RequestShape } from 'Modules/converter/getUriFromRequest';
import { getRequestFromUri, getUriFromRequest } from 'Modules/converter/index';
import { ITEM_ENDPOINT } from 'Modules/serverApi/endpoints';
import { item as getItemData, search as getSearch, track } from 'Modules/serverApi/index';
import { flushProductImpressions } from 'Modules/serverApi/track';
import type { AnyWidget } from 'Templates/widgets';
import { subscribeToStoragePermissions, waitForStoragePermissions } from 'Utils/browserStorage';
import {
  addProductsHistory,
  getTrackedTheme,
  isThemeTelemetryEnabled,
  removeItem,
  setTrackedTheme,
} from 'Utils/userStorage';
import keys from 'Utils/userStorageKeys';

import { initializeChunks } from './chunks';

import 'Stores/jtscycleparts2025/search.css';

type ContextWidgetType = 'SearchPage' | typeof ContextDialogName;

setShopifyVisitorId();
appendApi();
initializeVehicleStorage(store);
void waitForStoragePermissions().then(() => {
  subscribeToAnalyticsPermission();
  trackEvents();
});
initializeAddons(store);
renderWidgets();
initializeChunks();

if (requestConfig.isHtmxMode && requestConfig.hasHtmxResults) {
  synchronizeHtmx(window.location);
  store.dispatch(sendInitHtmxRequest());
} else if (requestConfig.needInitRequest) {
  setUriRequest(window.location, true);
}
subscribeToHistory();
makeInitRequests();

uiConfig.afterInit();
window.Convermax.initialized = true;

type StoreProviderProps = {
  children?: ComponentChildren;
};

type ReduxProviderChildren = Parameters<typeof Provider>[0]['children'];

function StoreProvider({ children }: StoreProviderProps) {
  return <Provider store={store}>{children as unknown as ReduxProviderChildren}</Provider>;
}

function isContextWidgetType(type: string): type is ContextWidgetType {
  return type === 'SearchPage' || type === ContextDialogName;
}

/**
 * `TemplateFunction<Params>` is a callable that returns a Preact `VNode`
 * tree. There is no runtime way to verify the *shape* of the return
 * value before invoking it (any function might be a template), so this
 * predicate only narrows on `typeof === 'function'`. The downstream
 * `ErrorBoundary` is the actual safety net for templates that throw or
 * return non-renderable values.
 */
function isTemplateFunction<Params>(value: unknown): value is TemplateFunction<Params> {
  return typeof value === 'function';
}

function getContextDialogCloseOnClickOutside(widget: AnyWidget): boolean | undefined {
  if (!('closeOnClickOutside' in widget)) {
    return undefined;
  }

  return typeof widget.closeOnClickOutside === 'boolean' ? widget.closeOnClickOutside : undefined;
}

function setShopifyVisitorId() {
  if (uiConfig.platform === 'shopify' && window.Convermax?.config?.webPixelEnabled) {
    window.Convermax.Shopify?.publishWebpixelEvent('use_shopify_visitor_id', {});
  }
}

function appendApi() {
  // Order matters: spread the existing merchant-page Convermax object
  // first so its `templates`, `config`, and other authoring fields
  // survive, then layer our typed SDK methods on top so a merchant
  // page cannot accidentally redefine `getSearchRequest` and friends.
  window.Convermax = {
    ...(window.Convermax || {}),
    version: CONVERMAX_VERSION,
    store: window.Convermax.store || {},
    trackProductView: () => {
      /* back compatibility */
    },
    trackAddToCart: (productId: string | number) => {
      if (requestConfig.isAnalyticsEnabled()) {
        track('AddToCart', String(productId));
      }
    },
    getSearchRequest: () => store.getState().search.request,
    getSearchResponse: () => store.getState().search.response,
    addProductsHistory: (product: unknown) => {
      if (isSearchItemRawData(product)) {
        addProductsHistory(product);
      }
    },
    createSearch: (request: RequestShape) => getUriFromRequest(request).search,
    createSearchUrl: (request: RequestShape) => getUriFromRequest(request).href,
    clearCurrentQuery: () => {
      const state = store.getState();

      if (autocompleteRequestQuerySelector(state)) {
        store.dispatch(clearAutocompleteQuery());
      }

      if (searchRequestQuerySelector(state)) {
        store.dispatch(discardQuery());
      }

      if (searchRequestFilterQuerySelector(state)) {
        store.dispatch(filterQueryResetRequested());
      }
    },
    clearCurrentRequest: (doNotDiscardVehicle?: boolean) => {
      store.dispatch(resetRequest({ discardVehicle: !doNotDiscardVehicle, allowSearchSameAsUri: true }));
    },
    getProductAsync: (id: string) => getItemData(id),
    openDialog: (dialogName: string, metadata: OpenDialogMetadata = {}) => {
      store.dispatch(dialogOpened(dialogName, metadata));
    },
    getRelatedItemsAsync: (id?: string | number, count?: number, sort?: string, extra?: RequestExtra) =>
      getSearch({
        selection: createSelectedVehicleSelectionSelector(store.getState()),
        pageSize: count,
        sort,
        extra: extra ? { ...extra, mode: 'RelatedItems' } : { itemId: id, mode: 'RelatedItems' },
      }).then((response) => response.Items.slice(0, count)),
    getSearchSort: () => store.getState().search.request.sort,
    setSearchSort: (sort: string) => {
      store.dispatch(setSort(sort));
    },
    discardSearchFields: (fields: string[]) => {
      store.dispatch(discardManyFields(fields));
    },
  };

  if (fitmentSearchEnabled) {
    if (fitmentSearchConfig.autoSyncConnected) {
      void loadAutoSyncVisualizerScript();
    }

    const fitmentSearchAPI = getFitmentSearchAPI(store);
    Object.assign(window.Convermax, fitmentSearchAPI);

    if (uiConfig.platform === 'bigcommerce') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      window.Convermax.addVehicleToOrder = (baseUrl, checkoutId, customerId) => {
        const vehicle = fitmentSearchAPI.getVehicleString();
        if (!checkoutId || !baseUrl || !vehicle) {
          return;
        }

        const vehicleNotes = `Vehicle: ${vehicle}.`;

        const options = {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customerMessage: vehicleNotes,
          }),
        };

        fetch(`${baseUrl}/api/storefront/checkouts/${checkoutId}`, options);
      };
    }
  }

  if (uiConfig.platform !== 'shopify') {
    delete window.Convermax.Shopify;
  }

  if (uiConfig.platform !== 'bigcommerce') {
    delete window.Convermax.BigCommerce;
  }
}

function trackEvents() {
  if (!requestConfig.isAnalyticsEnabled()) {
    return;
  }
  const { localItemId } = productConfig;

  if (localItemId) {
    // We use a WebPixel Extension for tracking "ProductView" on Shopify
    if (uiConfig.platform === 'shopify' && window.Convermax?.config?.webPixelEnabled) {
      return;
    }

    track('ProductView', localItemId);
  }

  /* eslint-disable camelcase */
  const { id, name, role, schema_name, schema_version } = uiConfig.themeInfo;

  const telemetryRate = mainConfig.telemetryRate ?? 0.01;

  if (
    !requestConfig.isLocalEnv &&
    !uiConfig.isInDesignMode &&
    id &&
    getTrackedTheme() !== id &&
    isThemeTelemetryEnabled(telemetryRate)
  ) {
    track('ThemeInfo', {
      ConnectedScript: jsScriptDomain,
      ThemeId: id,
      ThemeName: name,
      ThemeRole: role,
      ...(schema_name ? { ThemeSchemaName: schema_name } : {}),
      ...(schema_version ? { ThemeSchemaVersion: schema_version } : {}),
    });
    setTrackedTheme(id);
  }
}
/* eslint-enable camelcase */

function renderWidgets() {
  const widgetsWithContext: Record<ContextWidgetType, AnyWidget[]> = {
    SearchPage: [],
    [ContextDialogName]: [],
  };

  const templateContext: TemplateContextMap = {};

  widgetsConfig.forEach((widget) => {
    const { element, name, type, visible, isRoot } = widget;
    const template = 'template' in widget ? widget.template : undefined;

    if (!visible) {
      if (!isRoot) {
        templateContext[name] = () => null;
      }
      return;
    }

    if (isContextWidgetType(type)) {
      widgetsWithContext[type].push(widget);
      return;
    }

    const componentFromTemplate = makeTemplateApplicator(widget);

    if (componentFromTemplate) {
      if (isRoot && element && isTemplateFunction(template)) {
        const component = (
          <ErrorBoundary name={type}>
            <StoreProvider>
              {componentFromTemplate(template, { widgetName: name, items: undefined })}
            </StoreProvider>
          </ErrorBoundary>
        );
        render(component, element);
      } else if (!isRoot) {
        templateContext[name] = componentFromTemplate;
      }
    } else {
      console.warn(`Widget type ${type} not found`, widget);
    }
  });

  if (searchPageEnabled) {
    widgetsWithContext.SearchPage.forEach((widget) => {
      const template = 'template' in widget ? widget.template : undefined;
      if (widget.element && isTemplateFunction<SearchPageTemplateParams>(template)) {
        const props = { template, context: templateContext };
        render(
          <ErrorBoundary name="SearchPage">
            <StoreProvider>
              <SearchPage {...props} />
            </StoreProvider>
          </ErrorBoundary>,
          widget.element,
        );
      }
    });
  }

  if (contextDialogEnabled) {
    widgetsWithContext[ContextDialogName].forEach((widget) => {
      const template = 'template' in widget ? widget.template : undefined;
      if (widget.element && isTemplateFunction<ContextDialogTemplateParams>(template)) {
        const props = {
          template,
          context: templateContext,
          name: widget.name,
          closeOnClickOutside: getContextDialogCloseOnClickOutside(widget),
        };
        render(
          <ErrorBoundary name={ContextDialogName}>
            <StoreProvider>
              <ContextDialog {...props} />
            </StoreProvider>
          </ErrorBoundary>,
          widget.element,
        );
      }
    });
  }
}

function setUriRequest(location: Location | HistoryLocation, onInit = false) {
  store.dispatch(replaceRequest({ ...getRequestFromUri(location), fromUri: true, onInit }));
}

function synchronizeHtmx(location: Location | HistoryLocation) {
  const { query } = getRequestFromUri(location);
  store.dispatch(setAutocompleteQuery(query));
}

function subscribeToHistory() {
  history.listen(({ location, action }) => {
    if (action === 'REPLACE') {
      return;
    }

    if (location.state !== uriChangeHandled && requestConfig.isHtmxMode && requestConfig.hasHtmxResults) {
      synchronizeHtmx(location);
    } else if (location.state !== uriChangeHandled && requestConfig.hasSearchResults) {
      setUriRequest(location);
    } else {
      store.dispatch(replaceInHistory(location, null));
    }
  });
}

function makeInitRequests() {
  if (fitmentSearchConfig.needInitFitmentSearchRequest) {
    store.dispatch(sendInitFitmentSearchRequest());
  }

  const { localItemId } = productConfig;
  if (localItemId) {
    if (requestConfig.needInitItemRequest) {
      // item.json has no backup fallback, so network errors (including 429)
      // surface here directly. Consider adding GETBACKUP like search/fitments.
      getItemData(localItemId)
        .then(
          (item) => store.dispatch(setProductData(item)),
          (e) => logNetworkRequest({ endpoint: ITEM_ENDPOINT, scenario: 'request_error', primaryError: e }),
        )
        .catch((e) => logRuntimeError({ source: 'init_item_dispatch', error: e }));
    }

    if (fitmentSearchConfig.needInitFitmentTableRequest) {
      store.dispatch(
        sendFitmentTableRequest({
          productId: localItemId,
          variantId: productConfig.getVariantId?.(),
        }),
      );
    }

    if (fitmentSearchConfig.needInitVerifyFitmentRequest) {
      store.dispatch(sendVerifyFitmentRequest({ onInit: true }));
    }
  }
}

function subscribeToAnalyticsPermission(): void {
  const refreshAnalytics = () => {
    if (requestConfig.isAnalyticsEnabled()) {
      removeItem(keys.searchId);
    } else {
      flushProductImpressions();
    }
  };

  subscribeToStoragePermissions('analytics', refreshAnalytics);
}
